import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  MAT_LUXON_DATE_ADAPTER_OPTIONS,
  provideLuxonDateAdapter,
} from '@angular/material-luxon-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_ICON_DEFAULT_OPTIONS, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { Info } from 'luxon';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { apiErrorInterceptor } from './core/api/api-error-interceptor';
import { STORAGE } from './core/storage/storage-manager';
import { ThemeManager } from './core/theme/theme-manager';
import { AdminTitleStrategy } from './core/title-strategies/admin-title-strategy';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
    ),
    provideHttpClient(withFetch(), withInterceptors([authHttpInterceptorFn, apiErrorInterceptor])),
    provideLuxonDateAdapter({
      parse: {
        dateInput: ['D', 'dd-MM-yyyy', 'ddMMyyyy'],
        timeInput: 't',
      },
      display: {
        dateInput: 'D',
        timeInput: 't',
        monthYearLabel: 'LLL yyyy',
        dateA11yLabel: 'DD',
        monthYearA11yLabel: 'LLLL yyyy',
        timeOptionLabel: 't',
      },
    }),
    {
      provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
      useFactory: () => {
        const locale = inject<string>(MAT_DATE_LOCALE);
        const firstDayOfWeek = Info.getStartOfWeek({ locale });
        return { firstDayOfWeek };
      },
    },
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      useRefreshTokens: true,
      errorPath: 'acces-refuse',
      authorizationParams: {
        redirect_uri: document.baseURI,
        audience: environment.auth0.audience,
      },
      httpInterceptor: {
        allowedList: [`${environment.apiUrl}/*`],
      },
    }),
    provideEnvironmentInitializer(() => inject(ThemeManager)),
    provideAppInitializer(() => {
      const iconRegistry = inject(MatIconRegistry);
      const sanitizer = inject(DomSanitizer);

      iconRegistry.addSvgIconResolver((name, namespace) => {
        const iconPath = `icons/${namespace ? `${namespace}/` : ''}${name}.svg`;
        return sanitizer.bypassSecurityTrustResourceUrl(iconPath);
      });
    }),
    { provide: STORAGE, useValue: globalThis.localStorage },
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-outlined' },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    { provide: TitleStrategy, useClass: AdminTitleStrategy },
  ],
};
